import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import UserMenu from "../Admin/User/UserMenu";
import { NavTabList } from "../../components/NavTabs/NavTabs";
import { UserPageContainer } from "../../components/UserPageTemplate/UserPageTemplate";
import PrimaryLogo from "../../components/PrimaryLogo/PrimaryLogo";
import Button from "../../components/Button/Button";
import { useAlert } from "../../components/Alert/Alerts";
import config from "../../config";

import SubmissionLightbox from "./SubmisisonLightbox";
import { getOrder, updateOrderPDF } from "./manageCheckout";
import { OrderModel } from "./OrderInterfaces";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

const LoadingSpinner = styled.img`
	width: 44px;
	display: inline-flex;
`;

const Card = styled.div`
	border: solid 2px ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	padding: 1.5rem;
	border-radius: 8px;
`;

const Center = styled.div`
	max-width: 980px;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 2rem 0 4rem;
`;

type UiState = "loading" | "generating" | "success" | "failed" | "error";

function parseQuery(search: string) {
	const params = new URLSearchParams(search);

	const orderIdStr = params.get("orderId");
	const orderId = orderIdStr ? Number.parseInt(orderIdStr, 10) : NaN;
	const status = (params.get("status") || "").toLowerCase();

	return {
		orderId: Number.isFinite(orderId) ? orderId : null,
		status,
	};
}

function shouldGeneratePdf(order: OrderModel): boolean {
	// InvoicePDF is populated after upload; if missing, generate
	const invoice = (order as any).invoicePDF as string | undefined | null;
	return !invoice || invoice.trim().length === 0;
}

export default function PaymentzComplete() {
	const theme = useTheme();
	const history = useHistory();
	const location = useLocation();
	const { addNewAlert } = useAlert();
	const { settings } = useSelector((state: RootState) => state.settings);
	const countryIso = settings.countryIso;

	const { orderId, status } = useMemo(
		() => parseQuery(location.search),
		[location.search],
	);

	const [uiState, setUiState] = useState<UiState>("loading");
	const [workingText, setWorkingText] = useState("Finalizing your order…");
	const [details, setDetails] = useState<string>("");
	const [order, setOrder] = useState<OrderModel | null>(null);

	// Prevent duplicate execution in strict mode
	const startedRef = useRef(false);

	const goToCart = () => history.push("/entries/cart");
	const goToOrders = () => history.push("/entries/orders");

	const run = async () => {
		if (!orderId) {
			setUiState("error");
			setWorkingText("Missing order information");
			setDetails("No order id was provided.");
			return;
		}

		// Backend already determined final outcome.
		// Only failed / cancelled paths should redirect users back to cart.
		if (status === "failed" || status === "cancelled") {
			setUiState("failed");
			setWorkingText("Payment was not completed");
			setDetails(
				"Your payment did not complete. You can return to your cart and try again.",
			);
			return;
		}

		try {
			setUiState("loading");
			setWorkingText("Fetching order details…");

			const orderResponse = await getOrder(orderId);
			let currentOrder = orderResponse.data;

			setOrder(currentOrder);

			// Generate invoice PDFs if they do not exist yet
			if (shouldGeneratePdf(currentOrder)) {
				setUiState("generating");
				setWorkingText("Generating your invoice…");

				await updateOrderPDF(
					currentOrder,
					countryIso,
					currentOrder.taxId,
					settings.featureFlags["Invoice Notes"],
				);

				// Re-fetch order so invoice paths are populated
				const refreshed = await getOrder(orderId);
				currentOrder = refreshed.data;
				setOrder(currentOrder);
			}

			setUiState("success");
			setWorkingText("Your order is complete");
			setDetails("Opening your invoice…");

			// TODO (post-MVP):
			// If Paymentz inquiry API is added, this page can optionally
			// reconcile pending / 3D transactions before finalizing.
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Unable to finalize order.";

			setUiState("error");
			setWorkingText("We couldn’t finalize your order");
			setDetails(message);

			addNewAlert({
				type: "error",
				message:
					"We couldn’t finalize your order automatically. You can retry or check your Orders tab.",
			});
		}
	};

	useEffect(() => {
		// Clear Paymentz redirect flag so returning to Payment page doesn't reload
		sessionStorage.removeItem("paymentz_redirect");

		if (startedRef.current) return;
		startedRef.current = true;
		run();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId, status]);

	const showLightbox = uiState === "success" && order !== null;

	return (
		<div className="flex flex-col w-full">
			{showLightbox && <SubmissionLightbox order={order!} show={true} />}

			<UserMenu />
			<NavTabList>
				<div className="flex items-center mb-[66px]">
					<PrimaryLogo color={theme.colorLogoLightLight} width="254px" />
					<h1 className="ml-[5rem] text-colorCopyLightLight">
						Completing Checkout
					</h1>
				</div>
			</NavTabList>

			<UserPageContainer className="flex-1">
				<Center>
					<Card>
						<div className="flex items-center gap-[1rem]">
							{(uiState === "loading" || uiState === "generating") && (
								<LoadingSpinner src={config.assets.loading.primary} />
							)}
							<div className="flex flex-col">
								<h2 className="font-semibold">{workingText}</h2>
								{orderId && (
									<p className="mt-[0.25rem] opacity-80">Order #{orderId}</p>
								)}
							</div>
						</div>

						{details && <p className="mt-[1rem]">{details}</p>}

						<div className="flex gap-[1rem] mt-[1.5rem]">
							{(uiState === "failed" || uiState === "error") && (
								<>
									<Button className="button-light w-[220px]" onClick={goToCart}>
										Return to Cart
									</Button>
									<Button className="w-[220px]" onClick={run}>
										Retry
									</Button>
								</>
							)}

							{uiState === "success" && (
								<Button className="w-[220px]" onClick={goToOrders}>
									View Orders
								</Button>
							)}
						</div>
					</Card>
				</Center>
			</UserPageContainer>
		</div>
	);
}
